import * as XLSX from 'xlsx';

/**
 * Recursively extracts text content from JSX/React elements
 * @param {*} element - The element to extract text from
 * @returns {string} - The extracted text
 */
const extractTextFromJSX = (element) => {
  if (typeof element === 'string' || typeof element === 'number') {
    return element.toString();
  }
  
  if (Array.isArray(element)) {
    return element.map(extractTextFromJSX).join('');
  }
  
  if (element && typeof element === 'object') {
    // Handle React elements
    if (element.props) {
      if (element.props.children) {
        return extractTextFromJSX(element.props.children);
      }
    }
  }
  
  return '';
};

/**
 * Converts data into an Excel format with bold headers and triggers a download.
 * @param {Array<Object>} data The array of data objects (threads, connections, etc.) to export.
 * @param {Array<Object>} headers The array of header objects (from the respective table).
 * @param {string} fileName The desired name for the downloaded file (e.g., "ThreadsList").
 * @param {function(Object, string): string} valueMapper A function to map an object and a key to a displayable string value.
 */
export const exportToExcel = (data, headers, fileName, valueMapper) => {
  // 1. Filter out non-data columns (like the type icon/checkbox column)
  const exportHeaders = headers.filter(h => h.key !== 'type');
  const headerKeys = exportHeaders.map(h => h.key);

  // 2. Map JSX/React element headers to clean string labels
  const headerLabels = exportHeaders.map(h => {
    // If there's an explicit exportLabel, use it (best for complex headers)
    if (h.exportLabel) {
      return h.exportLabel;
    }
    
    // If label is already a simple string, use it
    if (typeof h.label === 'string') {
      return h.label;
    }
    
    // Otherwise, try to extract text from JSX
    const labelElement = h.label;
    const extractedText = extractTextFromJSX(labelElement);
    
    // Return the extracted text, or fall back to the key if extraction failed
    return extractedText || h.key;
  });

  // 3. Prepare data rows using the provided valueMapper
  const dataRows = data.map(item =>
    headerKeys.map(key => {
      let value = valueMapper(item, key);
      
      // Return raw values (XLSX will handle formatting)
      if (value === null || value === undefined) {
        return "";
      }
      return value;
    })
  );

  // 4. Combine headers and data
  const worksheetData = [headerLabels, ...dataRows];

  // 5. Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  // 6. Apply bold formatting to header row
  const range = XLSX.utils.decode_range(worksheet['!ref']);
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
    if (!worksheet[cellAddress]) continue;
    
    // Apply bold style to header cells
    worksheet[cellAddress].s = {
      font: { bold: true }
    };
  }

  // 7. Auto-size columns (optional, makes it look nicer)
  const colWidths = headerLabels.map((header, i) => {
    const headerLength = header.length;
    const maxDataLength = Math.max(
      ...dataRows.map(row => {
        const cellValue = row[i]?.toString() || '';
        return cellValue.length;
      })
    );
    return { wch: Math.max(headerLength, maxDataLength, 10) }; // Minimum width of 10
  });
  worksheet['!cols'] = colWidths;

  // 8. Create workbook and add worksheet
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

  // 9. Generate Excel file and trigger download
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
};